/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import { DangerButton, GhostButton } from '../../../../styles/ui/Button';
import { cancelScheduleItem } from '../../../../services/routing/routingApi';
import CancellationReasonPicker from '../../../../components/CancellationReasonPicker';

// =============================================================================
// Dialog de confirmação do cancelamento (soft delete) do item de rota.
//
// Reaproveitado pelas DUAS telas que excluem: a ficha (RoutingItemShow) e o
// board (RouteManagement/StopDrawer). Mexer aqui cobre os dois fluxos.
//
// Backend só aceita cancelar quando o item está PENDENTE — o pai já esconde o
// botão fora desse estado, mas o backend é a defesa real (422 fail).
//
// Handoff 0048 (breaking): a exclusão passou a EXIGIR um motivo de catálogo
// (cancellation_reason_id). O dialog mostra o select obrigatório e só habilita
// o "Sim, cancelar item" com um motivo escolhido — rastreabilidade ("toda
// exclusão tem um porquê").
//
// Handoff 0059: a reversão EXISTE — ação "Restaurar" (RestoreItemDialog),
// exclusiva de quem tem routing:schedule_item:cancel (o financeiro exclui mas
// não restaura). Restaurar apaga os metadados do cancelamento.
// =============================================================================

const Body = styled.div`
   font-size: 0.85rem;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   padding: 4px 0;
`;

const WarningLine = styled.p`
   margin: 0 0 12px;

   strong {
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
`;

const HintBox = styled.div`
   background: rgba(255, 193, 7, 0.1);
   border: 1px solid rgba(255, 193, 7, 0.4);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.78rem;
   color: #7d5a00;
   line-height: 1.5;
`;

const ReasonRow = styled.div`
   margin: 12px 0 4px;
`;

const ButtonRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 16px;
`;

export default function CancelItemDialog({
   isOpen,
   onClose,
   item,
   onCancelled,
}) {
   const [isSubmitting, setIsSubmitting] = useState(false);
   // Motivo da exclusão (handoff 0048) — id do catálogo, obrigatório.
   const [reasonId, setReasonId] = useState('');

   const mountedRef = useRef(true);
   useEffect(() => {
      mountedRef.current = true;
      return () => {
         mountedRef.current = false;
      };
   }, []);

   // Cada abertura começa sem motivo selecionado (o dialog não desmonta entre
   // aberturas — o estado persistiria de um item para o próximo sem isto).
   useEffect(() => {
      if (isOpen) setReasonId('');
   }, [isOpen]);

   const handleClose = useCallback(() => {
      if (isSubmitting) return;
      onClose?.();
   }, [isSubmitting, onClose]);

   const handleConfirm = async () => {
      if (isSubmitting || !item?.id || !reasonId) return;

      setIsSubmitting(true);
      try {
         const result = await cancelScheduleItem(item.id, Number(reasonId));
         toast.success(result.message || 'Item cancelado com sucesso.');
         onCancelled?.(result.scheduleItem);
         onClose?.();
      } catch (err) {
         const code = err?.response?.data?.errors?.[0]?.code;
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao cancelar item.';
         toast.error(apiMessage);

         // 422 de MOTIVO (sem motivo / motivo inválido ou inativo) é recuperável:
         // mantém o dialog aberto pra o operador corrigir e tentar de novo, sem
         // forçar reload do pai. Os demais 422 (item deixou de ser PENDENTE, etc.)
         // são race condition: o pai re-busca pra sincronizar a UI.
         const reasonValidationCodes = ['validation', 'invalid_cancellation_reason'];
         if (
            err?.response?.status === 422 &&
            !reasonValidationCodes.includes(code)
         ) {
            onCancelled?.(null);
         }
      } finally {
         if (mountedRef.current) setIsSubmitting(false);
      }
   };

   if (!isOpen || !item) return null;

   return (
      <Modal
         key={isOpen ? 'open' : 'closed'}
         isOpen={isOpen}
         onClose={handleClose}
         title="Cancelar item da rota"
         maxWidth="480px"
      >
         <Body>
            <WarningLine>
               Tem certeza que deseja cancelar o item{' '}
               <strong>
                  {item.order_number
                     ? `pedido ${item.order_number}`
                     : `#${item.id}`}
               </strong>
               ?
            </WarningLine>

            <HintBox>
               Esta ação registra o cancelamento (soft delete). O item deixa de
               aparecer nas listagens padrão e nos relatórios. A reversão
               (ação &ldquo;Restaurar&rdquo;, na ficha do item ou no card de
               itens excluídos do board) é exclusiva da equipe de roteirização.
            </HintBox>

            <ReasonRow>
               <CancellationReasonPicker
                  id="cancel-item-reason"
                  value={reasonId}
                  onChange={setReasonId}
                  labelText="Motivo da exclusão"
                  required
                  disabled={isSubmitting}
               />
            </ReasonRow>

            <ButtonRow>
               <GhostButton
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
               >
                  Voltar
               </GhostButton>
               <DangerButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting || !reasonId}
                  title={
                     !reasonId
                        ? 'Selecione o motivo da exclusão para continuar'
                        : undefined
                  }
               >
                  {isSubmitting ? 'Cancelando…' : 'Sim, cancelar item'}
               </DangerButton>
            </ButtonRow>
         </Body>
      </Modal>
   );
}

CancelItemDialog.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   item: PropTypes.object,
   onCancelled: PropTypes.func,
};
